import { useState, useEffect, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "../components/Header";
import ChunkCard from "../components/ChunkCard";
import HallucinationHighlighter from "../components/HallucinationHighlighter";
import WhyItFailedPanel from "../components/WhyItFailedPanel";
import ConfidenceChart from "../components/ConfidenceChart";
import FixSuggestions from "../components/FixSuggestions";
import FaithfulnessPanel from "../components/FaithfulnessPanel";
import ChunkAttribution from "../components/ChunkAttribution";
import ConfidenceBreakdown from "../components/ConfidenceBreakdown";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "../components/ui/breadcrumb";
import { Loader2, AlertCircle, CheckCircle2, AlertTriangle, Clock, Layers, FileText, MessageSquare, BarChart3, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  success: { icon: CheckCircle2, className: "text-green-600 bg-green-50", label: "Success" },
  warning: { icon: AlertTriangle, className: "text-yellow-600 bg-yellow-50", label: "Warning" },
  error: { icon: AlertCircle, className: "text-red-600 bg-red-50", label: "Error" },
  pending: { icon: Clock, className: "text-gray-600 bg-gray-50", label: "Analyzing..." }
};

export default function TraceDetail() {
  const { traceId } = useParams();
  const navigate = useNavigate();
  const [trace, setTrace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reEvaluating, setReEvaluating] = useState(false);

  const fetchTrace = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/v1/traces/${traceId}`, { withCredentials: true });
      setTrace(data);
    } catch (error) {
      toast.error("Trace not found");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  }, [traceId, navigate]);

  useEffect(() => {
    fetchTrace();
  }, [fetchTrace]);

  // Poll for evaluation updates if pending
  useEffect(() => {
    if (trace?.status === "pending") {
      const interval = setInterval(fetchTrace, 3000);
      return () => clearInterval(interval);
    }
  }, [trace?.status, fetchTrace]);

  const handleReEvaluate = async () => {
    setReEvaluating(true);
    try {
      await axios.post(`${API_URL}/api/v1/traces/${traceId}/re-evaluate`, {}, { withCredentials: true });
      toast.success("Re-evaluation started");
      // Poll for the result
      const poll = setInterval(async () => {
        const { data } = await axios.get(`${API_URL}/api/v1/traces/${traceId}`, { withCredentials: true });
        if (data.status !== "pending") {
          setTrace(data);
          setReEvaluating(false);
          clearInterval(poll);
        }
      }, 2000);
    } catch {
      toast.error("Failed to re-evaluate");
      setReEvaluating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!trace) return null;

  const config = statusConfig[trace.status] || statusConfig.pending;
  const StatusIcon = config.icon;
  const hasIssues = Array.isArray(trace.evaluation?.issues) && trace.evaluation.issues.length > 0;
  const highSeverityIssues = Array.isArray(trace.evaluation?.issues) 
    ? trace.evaluation.issues.filter(i => i.severity === "high") 
    : [];

  return (
    <div className="min-h-screen bg-background" data-testid="trace-detail-page">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Breadcrumb */}
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/dashboard">Dashboard</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to={`/projects/${trace.project_id}`}>Project</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="font-mono text-sm">
                {trace.id.slice(0, 8)}...
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Trace Header */}
        <div className="mb-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h1 className="text-xl font-bold font-heading mb-2" data-testid="trace-query">
                {trace.user_query}
              </h1>
              <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                <Badge variant="outline" className="font-mono">{trace.environment}</Badge>
                <span>•</span>
                <span>{trace.model_name}</span>
                <span>•</span>
                <span className="font-mono">{trace.latency_ms}ms</span>
                <span>•</span>
                <span>{format(new Date(trace.created_at), "MMM d, yyyy HH:mm:ss")}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleReEvaluate}
                disabled={reEvaluating || trace.status === "pending"}
                data-testid="re-evaluate-button"
              >
                {reEvaluating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                Re-evaluate
              </Button>
              <div className={`flex items-center gap-2 px-3 py-2 rounded-sm ${config.className}`}>
                <StatusIcon className="w-5 h-5" />
                <span className="font-semibold">{config.label}</span>
                {trace.evaluation?.overall_score !== undefined && (
                  <Badge variant="secondary" className="font-mono ml-2">
                    {trace.evaluation.overall_score}/100
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Issue Banners */}
        {highSeverityIssues.length > 0 && (
          <div className="space-y-2 mb-6" data-testid="issue-banners">
            {highSeverityIssues.map((issue, idx) => (
              <div key={idx} className="issue-banner issue-high flex items-start gap-3">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="font-semibold uppercase text-xs tracking-wider">{issue.type.replace(/_/g, ' ')}</span>
                  <p className="mt-1">{issue.description}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Why It Failed Panel */}
        {hasIssues && trace.evaluation?.why_it_failed && (
          <WhyItFailedPanel
            explanation={trace.evaluation.why_it_failed}
            score={trace.evaluation.overall_score}
          />
        )}

        {/* Split View */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Panel - Retrieved Context & Attribution */}
          <div className="space-y-6">
            <Card className="border border-border shadow-none" data-testid="chunks-panel">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-bold flex items-center gap-2">
                  <Layers className="w-5 h-5 text-muted-foreground" />
                  Retrieved Chunks ({trace.retrieved_chunks?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px] pr-4">
                  <div className="space-y-3">
                    {trace.retrieved_chunks?.map((chunk, idx) => {
                      const attr = trace.evaluation?.chunk_attributions?.find(a => a.chunk_index === idx);
                      return (
                        <ChunkCard
                          key={idx}
                          chunk={chunk}
                          index={idx}
                          hallucinated={trace.evaluation?.hallucinated_spans?.some(
                            span => chunk.content.includes(span)
                          )}
                          attribution={attr}
                        />
                      );
                    })}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Chunk Attribution */}
            {trace.evaluation?.chunk_attributions?.length > 0 && (
              <ChunkAttribution
                attributions={trace.evaluation.chunk_attributions}
                chunks={trace.retrieved_chunks}
              />
            )}

            {/* Confidence Visualization (legacy bar chart) */}
            {trace.retrieved_chunks?.length > 0 && (
              <Card className="border border-border shadow-none" data-testid="confidence-chart">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-bold flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-muted-foreground" />
                    Retrieval Confidence
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ConfidenceChart chunks={trace.retrieved_chunks} />
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Panel - Prompt, Response, Faithfulness & Confidence */}
          <div className="space-y-6">
            <Card className="border border-border shadow-none" data-testid="prompt-response-panel">
              <CardHeader className="pb-0">
                <Tabs defaultValue="response" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="response" className="flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" />
                      Response
                    </TabsTrigger>
                    <TabsTrigger value="prompt" className="flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Prompt
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="response" className="mt-4">
                    <div className="space-y-4">
                      <div className="bg-muted/50 rounded-sm p-4">
                        <HallucinationHighlighter
                          text={trace.model_response}
                          hallucinatedSpans={trace.evaluation?.hallucinated_spans || []}
                        />
                      </div>
                      {trace.evaluation?.hallucinated_spans?.length > 0 && (
                        <div className="text-sm text-muted-foreground">
                          <span className="inline-flex items-center gap-1">
                            <span className="w-3 h-3 bg-red-100 border-b-2 border-dashed border-red-500 rounded-sm" />
                            Highlighted text may not be supported by retrieved context
                          </span>
                        </div>
                      )}
                    </div>
                  </TabsContent>

                  <TabsContent value="prompt" className="mt-4">
                    <div className="relative">
                      {trace.token_count > 0 && (
                        <div className="absolute top-2 right-2 z-10">
                          <Badge variant="secondary" className="font-mono text-xs">
                            {trace.token_count} tokens
                          </Badge>
                        </div>
                      )}
                      <ScrollArea className="h-[300px]">
                        <pre className="prompt-viewer whitespace-pre-wrap">
                          {trace.constructed_prompt}
                        </pre>
                      </ScrollArea>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardHeader>
            </Card>

            {/* Faithfulness Panel */}
            <FaithfulnessPanel faithfulness={trace.evaluation?.faithfulness} />

            {/* Confidence Breakdown */}
            <ConfidenceBreakdown breakdown={trace.evaluation?.confidence_breakdown} />

            {/* Fix Suggestions */}
            {trace.evaluation?.fix_suggestions?.length > 0 && (
              <FixSuggestions suggestions={trace.evaluation.fix_suggestions} />
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
